"use client";

import { useState } from "react";
import { ChevronDown, List } from "lucide-react";

interface TocItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: readonly TocItem[];
}

/**
 * Collapsible inline table-of-contents for long-form articles. Closed
 * by default so the article body starts immediately on first paint —
 * a wall-of-text-y full ToC is what made earlier readers bounce.
 * Disclosure is a single tap to open for readers who want to skim.
 */
export function TableOfContents({ items }: TableOfContentsProps) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;

  return (
    <nav
      aria-label="Tabla de contenidos"
      className="mt-10 max-w-3xl rounded-2xl border border-ink-200 bg-white"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm font-semibold text-ink transition hover:bg-cream-50/50"
      >
        <span className="flex items-center gap-2">
          <List className="h-4 w-4 text-coral" aria-hidden />
          {open ? "Ocultar contenido" : `${items.length} secciones · ver índice`}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-ink-500 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <ol className="grid gap-x-6 gap-y-2 border-t border-ink-200 px-5 py-4 text-sm text-ink-700 sm:grid-cols-2">
          {items.map((item, idx) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="flex gap-3 underline-offset-4 hover:text-coral hover:underline"
              >
                <span className="w-6 shrink-0 text-right tabular-nums text-ink-500">
                  {idx + 1}.
                </span>
                <span>{item.label}</span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}
